import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AuthContextType, Profile } from "./types";
import { fetchUserProfile, updateUserProfile, updateUserRole } from "./authUtils";
import { defaultUserSettingsService } from "@/services/defaultUserSettingsService";
import {
  clearTeacherSessionBackup,
  reconcileImpersonationBackup,
} from "@/services/impersonationService";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Local-only chat/conversation cache keys that must be wiped on every
 *  sign-out (not only when the user clicks the Logout button) so the next
 *  session doesn't inherit a `tutor_current_conversation_id` belonging to
 *  the previous user — that mismatch is what produces PGRST116 in
 *  `useConversations.checkForSavedConversation`. */
const TUTOR_CACHE_KEYS = [
  "tutor_current_conversation_id",
  "tutor_last_messages",
  "tutor_last_model",
  "tutor_is_temporary_conversation",
];

const clearTutorLocalStorage = () => {
  try {
    for (const key of TUTOR_CACHE_KEYS) {
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.error("[AuthProvider.clearTutorLocalStorage] failed:", error);
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  /** True while fetching profile after session / user id change (e.g. teacher ↔ demo switch). */
  const [profileLoading, setProfileLoading] = useState(false);
  const [isDefaultPassword, setIsDefaultPassword] = useState(false);
  /** True when the session ended unexpectedly (token expired, refresh
   *  rotated by another device's sign-in, etc.). While true we keep
   *  user/session/profile in state so the page underneath the blocking
   *  SessionExpiredDialog stays mounted and the student can copy any
   *  in-progress work before clicking "Log in again". */
  const [sessionExpired, setSessionExpired] = useState(false);
  const { toast } = useToast();
  const profileInitializedRef = useRef(false);
  const userIdRef = useRef<string | null>(null);
  const authStateChangeDebounceRef = useRef<NodeJS.Timeout | null>(null);
  /** Set to `true` right before calling supabase.auth.signOut() so we can
   *  distinguish intentional logouts from session-expiry sign-outs. */
  const explicitSignOutRef = useRef(false);

  // Handle auth state changes with debouncing and stable user references
  useEffect(() => {
    const handleAuthStateChange = (event: string, currentSession: Session | null) => {
      // Clear any pending debounced updates
      if (authStateChangeDebounceRef.current) {
        clearTimeout(authStateChangeDebounceRef.current);
      }

      // Short debounce so rapid auth events (e.g. token refresh) coalesce.
      authStateChangeDebounceRef.current = setTimeout(async () => {
        const previousUserId = userIdRef.current;
        const currentUserId = currentSession?.user?.id ?? null;

        if (event === "SIGNED_OUT" || !currentSession) {
          // Wipe cached chat/conversation data on every sign-out path (not just
          // the explicit signOut() callback). Otherwise a stale
          // tutor_current_conversation_id from the previous session leaks into
          // the next sign-in on the same device and trips PGRST116.
          clearTutorLocalStorage();

          const isUnexpected = previousUserId !== null && !explicitSignOutRef.current;
          explicitSignOutRef.current = false;

          if (isUnexpected) {
            // DEFER clearing user/session/profile. The blocking
            // <SessionExpiredDialog/> is rendered on top of whatever page the
            // student was on; with state intact, route guards keep that page
            // mounted so the student can see/copy any unsaved work. State is
            // cleared in acknowledgeSessionExpired() when they confirm.
            setSessionExpired(true);
            return;
          }

          // Explicit sign-out (logout button) — wipe state immediately.
          userIdRef.current = null;
          setUser(null);
          setSession(null);
          setProfile(null);
          setProfileLoading(false);
          profileInitializedRef.current = false;
          setIsDefaultPassword(false);
          return;
        }

        // SIGNED_IN / TOKEN_REFRESHED / USER_UPDATED ...
        const userIdChanged = currentUserId !== previousUserId;

        if (userIdChanged) {
          userIdRef.current = currentUserId;
          setUser(currentSession?.user ?? null);
          profileInitializedRef.current = false;
          setProfile(null);
          setIsDefaultPassword(false);
          // A fresh sign-in supersedes any pending expiration banner.
          setSessionExpired(false);
        }

        setSession(currentSession);

        if (currentSession?.user && !profileInitializedRef.current) {
          setProfileLoading(true);
          try {
            const profileData = await fetchUserProfile(currentSession.user.id);
            if (profileData) {
              setProfile(profileData);
              if (profileData.role === "student") {
                const isDefault = await checkStudentDefaultPassword(
                  currentSession.user.id,
                  profileData
                );
                setIsDefaultPassword(isDefault);
              }
              profileInitializedRef.current = true;
            } else {
              console.error("Profile data is null for user:", currentSession.user.id);
              profileInitializedRef.current = true; // Mark as initialized even on failure to prevent loops
            }
          } catch (error) {
            console.error("Error fetching profile:", error);
            profileInitializedRef.current = true; // Mark as initialized even on error to prevent loops
          } finally {
            setProfileLoading(false);
          }
        }
      }, 32);
    };

    // First set up auth state change listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(handleAuthStateChange);

    // Then check for existing session
    supabase.auth
      .getSession()
      .then(async ({ data: { session: currentSession }, error: sessionError }) => {
        if (sessionError) {
          console.warn("[Auth] Session restore failed:", sessionError.message);
          const isRefreshTokenError =
            sessionError.message?.toLowerCase().includes("refresh token") ||
            sessionError.message?.toLowerCase().includes("refresh_token");
          if (isRefreshTokenError) {
            toast({
              title: "Platnost přihlášení vypršela",
              description:
                "Vaše relace vypršela. Přihlaste se prosím znovu. " +
                "Pokud problém přetrvává, zkuste vymazat data prohlížeče (cookies a úložiště) pro tuto stránku.",
              variant: "destructive",
              duration: 12000,
            });
          }
        }

        const currentUserId = currentSession?.user?.id ?? null;
        userIdRef.current = currentUserId;

        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user && !profileInitializedRef.current) {
          setProfileLoading(true);
          try {
            const profileData = await fetchUserProfile(currentSession.user.id);
            if (profileData) {
              setProfile(profileData);
              if (profileData.role === "student") {
                const isDefault = await checkStudentDefaultPassword(
                  currentSession.user.id,
                  profileData
                );
                setIsDefaultPassword(isDefault);
              }
              profileInitializedRef.current = true;
            } else {
              console.error("Initial profile data is null for user:", currentSession.user.id);
              profileInitializedRef.current = true; // Mark as initialized even on failure
            }
          } catch (error) {
            console.error("Error fetching initial profile:", error);
            profileInitializedRef.current = true; // Mark as initialized even on error
          } finally {
            setProfileLoading(false);
          }
          // Profile initialization complete
        }

        setLoading(false);
      });

    return () => {
      subscription.unsubscribe();
      if (authStateChangeDebounceRef.current) {
        clearTimeout(authStateChangeDebounceRef.current);
      }
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      console.error("Chyba při přihlašování:", error);
      throw error;
    }
  }, []);

  const signUp = useCallback(
    async (email: string, password: string) => {
      try {
        const { data, error } = await supabase.auth.signUp({ email, password });

        if (error) {
          toast({
            title: "Registrace selhala",
            description: error.message,
            variant: "destructive",
          });
          throw error;
        }

        // Pokud se uživatel úspěšně zaregistroval a má ID, aplikuj výchozí nastavení rolí
        if (data.user?.id) {
          try {
            await defaultUserSettingsService.applyDefaultRolesToUser(data.user.id);
          } catch (roleError) {
            console.error("Error applying default roles:", roleError);
            // Nezastavujeme proces registrace kvůli chybě rolí
          }
        }

        toast({
          title: "Účet vytvořen",
          description:
            "Váš účet byl úspěšně vytvořen. Zkontrolujte svůj e-mail pro potvrzovací odkaz.",
        });
      } catch (error) {
        console.error("Registration error:", error);
        throw error;
      }
    },
    [toast]
  );

  const signOut = useCallback(async () => {
    try {
      explicitSignOutRef.current = true;
      // A parked teacher session must never survive an explicit logout —
      // otherwise the next person on this device could restore it.
      clearTeacherSessionBackup();
      await supabase.auth.signOut();

      // Then clear local state
      setSession(null);
      setUser(null);
      setProfile(null);
      setProfileLoading(false);
      setIsDefaultPassword(false);
      profileInitializedRef.current = false;
      userIdRef.current = null;

      // Clear cached tutor/conversation data (also done on every SIGNED_OUT
      // path; called here too so explicit-logout side effects are obvious).
      clearTutorLocalStorage();

      // Clear ALL Supabase-related keys to prevent session restoration
      try {
        const allKeys = Object.keys(localStorage);
        const supabaseKeys = allKeys.filter(
          (key) =>
            key.startsWith("sb-") ||
            key.includes("supabase") ||
            key.includes("auth-token") ||
            key.includes("refresh-token")
        );

        supabaseKeys.forEach((key) => {
          localStorage.removeItem(key);
        });
      } catch (e) {
        console.error("Error clearing localStorage items on sign out:", e);
      }

      toast({
        title: "Odhlášeno",
        description: "Byli jste úspěšně odhlášeni.",
      });
    } catch (error) {
      console.error("Chyba při odhlašování:", error);
      throw error;
    }
  }, [toast]);

  const updateProfile = useCallback(
    async (updates: Partial<Profile>) => {
      if (!user) {
        toast({
          title: "Chyba",
          description: "Pro aktualizaci profilu musíte být přihlášeni.",
          variant: "destructive",
        });
        return;
      }

      try {
        await updateUserProfile(user.id, updates);
        // Refetch profile to ensure we have the latest data from database
        const updatedProfile = await fetchUserProfile(user.id);
        if (updatedProfile) {
          setProfile(updatedProfile);
        } else {
          // Fallback to local update if refetch fails
          setProfile((prev) => (prev ? { ...prev, ...updates } : null));
        }

        // Removed the "Profil aktualizován" toast notification
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Neznámá chyba";
        toast({
          title: "Aktualizace profilu selhala",
          description: errorMessage,
          variant: "destructive",
        });
        console.error("Chyba při aktualizaci profilu:", error);
        throw error;
      }
    },
    [user, toast]
  );

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    try {
      const refreshedProfile = await fetchUserProfile(user.id);
      if (refreshedProfile) {
        setProfile(refreshedProfile);
      }
    } catch (error) {
      console.error("Error refreshing profile:", error);
    }
  }, [user]);

  const updateRole = useCallback(
    async (role: "student" | "teacher" | "admin") => {
      if (!user) {
        toast({
          title: "Chyba",
          description: "Pro změnu role musíte být přihlášeni.",
          variant: "destructive",
        });
        return;
      }

      try {
        await updateUserRole(role);
        setProfile((prev) => (prev ? { ...prev, role } : null));

        toast({
          title: "Role změněna",
          description: `Vaše role byla nastavena na ${role}.`,
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Neznámá chyba";
        toast({
          title: "Chyba při změně role",
          description: errorMessage,
          variant: "destructive",
        });
        console.error("Chyba při aktualizaci role:", error);
        throw error;
      }
    },
    [user, toast]
  );

  /**
   * Checks whether a student is currently using the printed default password
   * and should be forced through /change-password.
   *
   * Takes the already-fetched profile so we don't issue a second `profiles`
   * round-trip per auth event (test-student detection used to do that).
   */
  const checkStudentDefaultPassword = async (
    studentId: string,
    profileData: Profile | null
  ): Promise<boolean> => {
    try {
      // Test students are allowed to keep default password
      if (profileData?.is_test_student) {
        return false;
      }

      const { data, error } = await supabase
        .from("student_login_codes")
        .select("is_active, default_password")
        .eq("student_id", studentId)
        .eq("is_active", true)
        .maybeSingle();

      if (error) {
        console.error("[AuthProvider.checkStudentDefaultPassword] query failed:", error);
        return false;
      }

      if (!data) {
        return false;
      }

      return data.is_active && !!data.default_password;
    } catch (error) {
      console.error("[AuthProvider.checkStudentDefaultPassword] unexpected error:", error);
      return false;
    }
  };

  const updateDefaultPasswordStatus = useCallback((isDefault: boolean) => {
    setIsDefaultPassword(isDefault);
  }, []);

  /** Confirms the SessionExpiredDialog: clears the deferred auth state so
   *  route guards can naturally redirect to /auth. Called only from the
   *  dialog's "Log in again" button. */
  const acknowledgeSessionExpired = useCallback(() => {
    setSessionExpired(false);
    userIdRef.current = null;
    setUser(null);
    setSession(null);
    setProfile(null);
    setProfileLoading(false);
    profileInitializedRef.current = false;
    setIsDefaultPassword(false);
  }, []);

  // Impersonation hygiene: once the active identity is provably a non-demo
  // account again (e.g. fresh login after the demo session expired), drop any
  // stale parked teacher session. While impersonating, the profile is a test
  // student so the backup is left untouched.
  useEffect(() => {
    if (profile && user) {
      reconcileImpersonationBackup(user.id, profile.is_test_student === true);
    }
  }, [profile, user]);

  // Memoize the context value to prevent unnecessary re-renders
  // Only re-memoize when essential values change (user.id instead of full user object)
  const effectiveLoading = loading || profileLoading;

  const contextValue = useMemo(
    () => ({
      session,
      user,
      profile,
      loading: effectiveLoading,
      isDefaultPassword,
      sessionExpired,
      acknowledgeSessionExpired,
      signIn,
      signUp,
      signOut,
      updateProfile,
      refreshProfile,
      updateRole,
      updateDefaultPasswordStatus,
    }),
    [
      session,
      user?.id,
      profile?.id,
      profile?.role,
      profile?.points,
      effectiveLoading,
      isDefaultPassword,
      sessionExpired,
      acknowledgeSessionExpired,
      signIn,
      signUp,
      signOut,
      updateProfile,
      refreshProfile,
      updateRole,
      updateDefaultPasswordStatus,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
